export interface DispositionOption {
  value: string
  label: string
  desc: string
  tone: string
}

// The 8 SDR call dispositions shown on the disposition screen.
export const DISPOSITIONS: DispositionOption[] = [
  { value: 'connected_interested',     label: 'Interested',     desc: 'Real conversation, prospect engaged',     tone: '#16A34A' },
  { value: 'connected_not_interested', label: 'Not interested', desc: 'Conversation, prospect declined',          tone: '#CA8A04' },
  { value: 'connected_callback',       label: 'Call back',      desc: 'Asked us to call back later',             tone: '#0093DA' },
  { value: 'wrong_number',             label: 'Wrong number',   desc: 'This number is incorrect so we will try the next one', tone: '#6B7280' },
  { value: 'gatekeeper',               label: 'Gatekeeper',     desc: 'Spoke to a gatekeeper, not the prospect', tone: '#7C3AED' },
  { value: 'do_not_call',              label: 'Do not call',    desc: 'Prospect asked us to stop calling', tone: '#DC2626' },
  { value: 'voicemail_left',           label: 'Voicemail left', desc: 'Left a voicemail myself',                 tone: '#0EA5E9' },
  { value: 'no_answer',                label: 'No answer',      desc: 'Rang out / no contact made',              tone: '#475569' },
]

export function dispositionLabel(code: string | null | undefined): string | undefined {
  return DISPOSITIONS.find((d) => d.value === code)?.label
}

// History-page disposition label. Covers the rich SDR codes (with the "Connected · "
// prefix the call-history view uses), the system-set codes that aren't on the
// disposition screen — `answered` (a pickup the rep never dispositioned) and `skipped`
// (a skip is a real dial, never hidden behind a dash) — and the coarse dispatch outcomes
// (`human_picked_up` is an agent pickup). `null` → the "——" empty cell.
// The pill's COLOR comes from StatusPill keyed on the raw code; this is only the text.
export function dispositionDisplayLabel(code: string | null | undefined): string {
  if (!code) return '——'
  switch (code) {
    case 'human_picked_up':
      return 'Connected'
    case 'answered':
      return 'Answered'
    case 'answered_unlogged':
      return 'Answered · not logged'
    case 'connected_interested':
      return 'Connected · interested'
    case 'connected_callback':
      return 'Connected · callback set'
    case 'connected_not_interested':
      return 'Connected · not interested'
    case 'gatekeeper':
      return 'Gatekeeper'
    case 'wrong_number':
      return 'Wrong number'
    case 'do_not_call':
      return 'Do not call'
    case 'voicemail_left':
      return 'Voicemail left'
    case 'machine_detected':
      return 'Machine detected'
    case 'no_answer':
      return 'No answer'
    case 'busy':
      return 'Busy'
    case 'skipped':
      return 'Skipped'
    case 'unreachable':
      return 'Unreachable'
    case 'not_available':
      return 'Not available'
    case 'call_failed':
      return 'Call failed'
    default:
      return dispositionLabel(code) || code.replace(/_/g, ' ')
  }
}

// Best-matching AppIcon key per disposition/outcome — the history pills carry an icon,
// mirroring the mockup. Falls back to the phone glyph.
export function dispositionIcon(code: string | null | undefined): string {
  switch (code) {
    case 'connected_callback':
      return 'followup'
    case 'voicemail_left':
    case 'machine_detected':
      return 'voicemail'
    case 'do_not_call':
    case 'wrong_number':
      return 'error'
    case 'unreachable':
    case 'not_available':
    case 'call_failed':
      return 'phone_off'
    case 'gatekeeper':
      return 'buying_team'
    case 'skipped':
      return 'wait'
    default:
      return 'phone_call'
  }
}

// A failed dial carries no disposition.
export function failureDisplayCode(errorClass: string | null | undefined): string | null {
  if (!errorClass) return null
  if (errorClass === 'invalid_number') return 'unreachable'
  if (errorClass === 'destination_not_enabled') return 'not_available'
  return 'call_failed'
}

export type DispositionTone = 'connected' | 'callback' | 'voicemail' | 'danger' | 'neutral'

// Pill tone per disposition, mapped onto the app's status palette (green / blue / amber /
// red / gray) so the page stays consistent with the rest of the app while matching the
// mockup's semantic grouping (connected=positive, callback=info, voicemail=warm,
// not-interested/DNC=danger, the rest neutral).
export function dispositionTone(code: string | null | undefined): DispositionTone {
  switch (code) {
    case 'connected_interested':
    case 'human_picked_up':
      return 'connected'
    case 'connected_callback':
      return 'callback'
    case 'voicemail_left':
      return 'voicemail'
    case 'connected_not_interested':
    case 'do_not_call':
      return 'danger'
    default:
      return 'neutral'
  }
}
